import { Box } from '@mui/material';
import { useMemo } from 'react';
import type { FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { useFlowControlSimulationCueStore } from '@/state/stores/use-flow-control-simulation-cue-store';
import { useFlowControlMediaExpandStore } from '@/state/stores/use-flow-control-media-expand-store';
import { useFlowControlLiveState } from '../flow-control-live-state';
import { liveCueSheetToSimulationRows } from '@/helpers/live-cue-sheet-to-simulation-rows.helper';
import {
  appendEmptyEffectiveFlowSlots,
  foldInBetweenIntoPrecedingSections,
  groupSheetRows,
  omitUntimedSlotsFromRundown,
  retainFullySkippedFlowPlaylistSections,
} from '@/helpers/group-cue-sheet-rundown.helper';
import { buildInBetweenPlayoutSegmentId } from '@/helpers/flow-control-in-between-playout.helper';
import {
  resolveRundownSectionAnchor,
  resolveRundownSectionExpandId,
  resolveRundownSectionMediaSlot,
  resolveRundownSectionSlotId,
} from '@/helpers/cue-sheet-rundown-section.helper';
import { resolveFlowControlExpandSegmentMeta } from '@/helpers/flow-control-expand-segment-meta.helper';
import { getSlotAssetsDetailsTitle } from '../edit-details/slot-assets-details-title';
import { formatSessionSegmentIndexLabel } from '../slot-list/session-segment.helpers';
import { FlowControlPlayoutSegmentRow } from '../slot-list/flow-control-playout-segment-row';
import { ConfidenceRundownProvider } from '../slot-list/confidence-rundown-provider';
import { isFlowControlInBetweenSegmentKind, type FlowControlSegmentKind } from '@/helpers/simulation-cue-engine.helper';
import { resolveRundownSegmentTiming } from '@/helpers/rundown-segment-timing.helper';
import { useTranslations } from '@/i18n/use-translations';

export function FlowControlCueSheetRundown({
  onSlotSelect,
}: {
  onSlotSelect?: (slot: FlowSlotDto, isUserInitiated?: boolean) => void;
}) {
  const t = useTranslations();
  const flowPlan = useFlowStore((s) => s.flowPlan);
  const flow = useFlowStore((s) => s.flow);
  const effectiveFlow = flowPlan ?? flow;
  const { performMode, cueSheet } = useFlowControlLiveState();
  const simulationRows = useFlowControlSimulationCueStore((s) => s.rows);
  const expandId = useFlowControlMediaExpandStore((s) => s.expandId);
  const setExpandId = useFlowControlMediaExpandStore((s) => s.setExpandId);

  const rows = useMemo(() => {
    if (performMode === 'play' && cueSheet) {
      return liveCueSheetToSimulationRows(cueSheet);
    }
    return simulationRows;
  }, [cueSheet, performMode, simulationRows]);

  const sections = useMemo(() => {
    const grouped = retainFullySkippedFlowPlaylistSections(groupSheetRows(rows), effectiveFlow);
    const folded = foldInBetweenIntoPrecedingSections(grouped);
    const withEmpty = performMode === 'play' ? appendEmptyEffectiveFlowSlots(folded, effectiveFlow?.slots) : folded;
    return omitUntimedSlotsFromRundown(withEmpty, effectiveFlow?.slots);
  }, [effectiveFlow, performMode, rows]);

  return (
    <ConfidenceRundownProvider>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: (theme) => `calc(${theme.spacing(0.5)} + 4px)` }}>
        {sections.map((section) => {
          const segmentId = resolveRundownSectionExpandId(section);
          const joinSlotId = resolveRundownSectionSlotId(section);
          const joinSlot = joinSlotId ? effectiveFlow?.slots?.find((entry) => entry.id === joinSlotId) : undefined;
          const slot = resolveRundownSectionMediaSlot(section, effectiveFlow?.slots);
          const meta = resolveFlowControlExpandSegmentMeta(segmentId, joinSlot ?? null, t, effectiveFlow?.slots);
          let label = meta?.label;
          if (!label && joinSlot) {
            label = getSlotAssetsDetailsTitle(joinSlot);
          }
          if (joinSlot && effectiveFlow && label) {
            const segmentLabel = formatSessionSegmentIndexLabel(effectiveFlow, joinSlot);
            if (segmentLabel) {
              label = `${label} (${segmentLabel})`;
            }
          }
          label = label || section.segmentKind;
          const trailingExpandId = section.trailingInBetween
            ? buildInBetweenPlayoutSegmentId(section.trailingInBetween.segmentKind, section.trailingInBetween.slotId)
            : undefined;
          const isExpanded = expandId === segmentId || (trailingExpandId != null && expandId === trailingExpandId);
          const segmentKind = (section.segmentKind as FlowControlSegmentKind) ?? 'session';
          const timingSlot = isFlowControlInBetweenSegmentKind(segmentKind) ? null : (joinSlot ?? slot);
          const timing = resolveRundownSegmentTiming(segmentKind, timingSlot, effectiveFlow);
          return (
            <FlowControlPlayoutSegmentRow
              key={section.key}
              label={label}
              segmentAnchor={resolveRundownSectionAnchor(section)}
              segmentKind={segmentKind}
              segmentId={segmentId}
              slotId={joinSlotId}
              isExpanded={isExpanded}
              disableManualStop={isFlowControlInBetweenSegmentKind(segmentKind)}
              normalSlotChrome={Boolean(slot) && !isFlowControlInBetweenSegmentKind(segmentKind)}
              slot={slot}
              showTimingColumn
              startTime={timing.startTime}
              durationLabel={timing.durationLabel}
              trailingInBetween={section.trailingInBetween}
              showSkippedPlaylist={Boolean(section.playlistSkipped)}
              onToggleExpand={() => {
                setExpandId(isExpanded ? null : segmentId, 'manual');
                if (joinSlot) {
                  onSlotSelect?.(joinSlot, true);
                }
              }}
            />
          );
        })}
      </Box>
    </ConfidenceRundownProvider>
  );
}
